import React, { useMemo, useState } from 'react';

import { Box, FormControl, Grid, InputLabel, MenuItem, Select } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { AssetItem, RecommendedAsset } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

import { ActionLibraryInstanceFormValues } from '../../../ActionLibraryInstanceForm.types';

import AssetSearchField from './components/AssetSearchField';
import AssetSection from './components/AssetSection';
import LoadingState from './components/LoadingState';
import useAssetFiltering from './hooks/useAssetFiltering';
import useAssetSelection from './hooks/useAssetSelection';
import useAssetSelectionData from './hooks/useAssetSelectionData';
import { useAssetToggleHandlers } from './hooks/useAssetToggleHandlers';
import useAutoSelectRecommended from './hooks/useAutoSelectRecommended';
import { useImportanceFilterOptions } from './hooks/useImportanceFilterOptions';
import type { ImportanceOption } from './hooks/useImportanceFilterOptions';
import { FILTER_IMPORTANCE_NONE } from './utils/assetFilters';

const AssetSelectionList: React.FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<ActionLibraryInstanceFormValues>();
  const [search, setSearch] = useState('');
  const [importance, setImportance] = useState('all');

  const { selectedTemplate } = values;
  const templateId = selectedTemplate?.id;

  const { recommendedAssets, availableAssets, isLoading } = useAssetSelectionData({
    templateId,
    search,
  });

  const {
    selectedAssets,
    toggleRecommended,
    toggleAvailable,
    selectAll: handleSelectAll,
    deselectAll: handleDeselectAll,
    addAssets,
  } = useAssetSelection();

  useAutoSelectRecommended({
    recommendedAssets,
    isLoading,
    templateId,
    onAddAssets: addAssets,
  });

  const { filteredRecommended, filteredOthers } = useAssetFiltering({
    recommendedAssets,
    availableAssets,
    search,
    importance,
  });

  const selectedAssetIds = useMemo(() => selectedAssets.map(a => Number(a.id)), [selectedAssets]);
  const importanceOptions = useImportanceFilterOptions();
  const { handleToggleRecommended, handleToggleAvailable } = useAssetToggleHandlers({
    toggleRecommended,
    toggleAvailable,
  });

  const sections = useMemo(() => {
    const list: Array<{
      key: string;
      assets: (RecommendedAsset | AssetItem)[];
      emptyMessage: string[];
      isRecommended: boolean;
      title: string;
      onSelectAll: () => void;
      onDeselectAll: () => void;
      onToggle: (asset: RecommendedAsset | AssetItem) => void;
    }> = [];

    if (templateId) {
      list.push({
        key: 'recommended',
        assets: filteredRecommended,
        emptyMessage:
          action_library_messages.instance.asset_selection.no_recommended_assets ||
          action_library_messages.instance.asset_selection.no_assets_found,
        isRecommended: true,
        title: t(action_library_messages.instance.asset_selection.recommended_section),
        onSelectAll: () => handleSelectAll(filteredRecommended),
        onDeselectAll: () => handleDeselectAll(filteredRecommended),
        onToggle: handleToggleRecommended,
      });
    }

    list.push({
      key: 'others',
      assets: filteredOthers,
      emptyMessage: action_library_messages.instance.asset_selection.no_assets_found,
      isRecommended: false,
      title: t(action_library_messages.instance.asset_selection.others_section),
      onSelectAll: () => handleSelectAll(filteredOthers),
      onDeselectAll: () => handleDeselectAll(filteredOthers),
      onToggle: handleToggleAvailable,
    });

    return list;
  }, [
    templateId,
    filteredRecommended,
    filteredOthers,
    handleSelectAll,
    handleDeselectAll,
    handleToggleRecommended,
    handleToggleAvailable,
    t,
  ]);

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Box>
      <Grid alignItems='center' container spacing={2}>
        <Grid item md={8} xs={12}>
          <AssetSearchField onSearchChange={setSearch} search={search} />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormControl fullWidth size='small' variant='outlined'>
            <InputLabel id='importance-select-label'>{t(action_library_messages.filters.asset_importance)}</InputLabel>
            <Select
              label={t(action_library_messages.filters.asset_importance)}
              labelId='importance-select-label'
              onChange={e => setImportance(e.target.value as string)}
              value={importance}
            >
              <MenuItem value='all'>
                <em>{t(action_library_messages.instance.form.all_importances)}</em>
              </MenuItem>
              <MenuItem value={FILTER_IMPORTANCE_NONE}>{t(action_library_messages.instance.form.importance_not_set)}</MenuItem>
              {importanceOptions.map((opt: ImportanceOption) => (
                <MenuItem key={opt.key} value={opt.key}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {sections.map(section => (
        <AssetSection
          key={section.key}
          assets={section.assets}
          emptyMessage={section.emptyMessage}
          isRecommended={section.isRecommended}
          onDeselectAll={section.onDeselectAll}
          onSelectAll={section.onSelectAll}
          onToggle={section.onToggle}
          selectedAssetIds={selectedAssetIds}
          title={section.title}
        />
      ))}
    </Box>
  );
};

export default AssetSelectionList;
